/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from '../../../message';
import { IDescriptorBody } from '../../../descriptor';
import CommentDescriptor, { IComment, ICommentJSON } from '../../descriptors/commentdescriptor';

export interface IAddCommentThreadMessage extends IMessage {
	commentThreadId: string;
	documentId: string;
	context: string | null | undefined;
	resolvedAt: string;
	createdAt: string;
	resolvedBy: string | null;
	deletedAt: string;
	attributes: string | null;
	comments: ICommentJSON[];
}

export default class AddCommentThreadMessage extends Message {
	public readonly createdAt: Date | string;

	public readonly resolvedAt: Date | string;

	public readonly deletedAt: Date | string;

	public static readonly TYPE: string = '140';

	public static readonly READABLE_TYPE_NAME: string = 'addCommentThread';

	public static readonly DESCRIPTOR_NAME: string = 'CommentsV2AddCommentThreadMessage';

	public static readonly DESCRIPTOR: IDescriptorBody = {
		fields: {
			documentId: {
				type: 'string',
				id: 1
			},
			commentThreadId: {
				type: 'string',
				id: 2
			},
			context: {
				type: 'string',
				id: 3
			},
			createdAt: {
				type: 'string',
				id: 4
			},
			resolvedAt: {
				type: 'string',
				id: 5
			},
			resolvedBy: {
				type: 'string',
				id: 6
			},
			attributes: {
				type: 'string',
				id: 7
			},
			deletedAt: {
				type: 'string',
				id: 8
			},
			comments: {
				type: 'CommentDescriptor',
				rule: 'repeated',
				id: 9
			}
		}
	};

	public constructor(
		public readonly documentId: string,
		public readonly commentThreadId: string,
		public readonly comments: IComment[] = [],
		public readonly context?: Record<string, unknown> | null,
		public readonly resolvedBy: string | null = null,
		createdAt: string | Date | null = null,
		resolvedAt: string | Date | null = null,
		deletedAt: string | Date | null = null,
		public readonly attributes: Record<string, unknown> | null = null
	) {
		super();

		this.createdAt = createdAt ? new Date( createdAt ) : '';
		this.resolvedAt = resolvedAt ? new Date( resolvedAt ) : '';
		this.deletedAt = deletedAt ? new Date( deletedAt ) : '';
	}

	public toJSON(): IAddCommentThreadMessage {
		return {
			commentThreadId: this.commentThreadId,
			context: this.context ? JSON.stringify( this.context ) : this.context,
			documentId: this.documentId,
			resolvedBy: this.resolvedBy,
			// instanceof operator can be safely used on node constructors e.g Date
			/* eslint-disable no-restricted-syntax */
			resolvedAt: this.resolvedAt instanceof Date ? this.resolvedAt.toISOString() : this.resolvedAt,
			deletedAt: this.deletedAt instanceof Date ? this.deletedAt.toISOString() : this.deletedAt,
			createdAt: this.createdAt instanceof Date ? this.createdAt.toISOString() : this.createdAt,
			/* eslint-enable no-restricted-syntax */
			attributes: this.attributes ? JSON.stringify( this.attributes ) : this.attributes,
			comments: this.comments?.map( value => CommentDescriptor.toJSON( value ) )
		};
	}

	public static fromJSON( object: IAddCommentThreadMessage ): AddCommentThreadMessage {
		return new AddCommentThreadMessage(
			object.documentId,
			object.commentThreadId,
			object.comments?.map( value => CommentDescriptor.fromJSON(
				{
					...value,
					documentId: object.documentId,
					commentThreadId: object.commentThreadId
				}
			) ),
			object.context ? JSON.parse( object.context ) : object.context,
			object.resolvedBy,
			object.createdAt ? new Date( object.createdAt ) : undefined,
			object.resolvedAt ? new Date( object.resolvedAt ) : undefined,
			object.deletedAt ? new Date( object.deletedAt ) : undefined,
			object.attributes ? JSON.parse( object.attributes ) : object.attributes
		);
	}
}
